import { CountryResponse, CountryCode } from '@oncampusabroad/api';
import { store } from '../redux/store';
import { fetchCountries } from '../redux/api-v2';
import { getLanguage } from './i18n-utils';

export type Country = {
  c: string;
  en: string;
  es: string;
};

const countryNameMapping: Record<string, { en: string; es: string }> = {
  'AT': { en: 'Austria', es: 'Austria' },
  'BE': { en: 'Belgium', es: 'Bélgica' },
  'BG': { en: 'Bulgaria', es: 'Bulgaria' },
  'CH': { en: 'Switzerland', es: 'Suiza' },
  'RS': { en: 'Serbia', es: 'Serbia' },
  'CY': { en: 'Cyprus', es: 'Chipre' },
  'CZ': { en: 'Czech Republic', es: 'República Checa' },
  'DE': { en: 'Germany', es: 'Alemania' },
  'DK': { en: 'Denmark', es: 'Dinamarca' },
  'EE': { en: 'Estonia', es: 'Estonia' },
  'GR': { en: 'Greece', es: 'Grecia' },
  'ES': { en: 'Spain', es: 'España' },
  'FI': { en: 'Finland', es: 'Finlandia' },
  'FR': { en: 'France', es: 'Francia' },
  'GB': { en: 'United Kingdom', es: 'Reino Unido' },
  'HR': { en: 'Croatia', es: 'Croacia' },
  'HU': { en: 'Hungary', es: 'Hungría' },
  'IE': { en: 'Ireland', es: 'Irlanda' },
  'IS': { en: 'Iceland', es: 'Islandia' },
  'IT': { en: 'Italy', es: 'Italia' },
  'LI': { en: 'Liechtenstein', es: 'Liechtenstein' },
  'LT': { en: 'Lithuania', es: 'Lituania' },
  'LU': { en: 'Luxembourg', es: 'Luxemburgo' },
  'LV': { en: 'Latvia', es: 'Letonia' },
  'MK': { en: 'The Republic of North Macedonia', es: 'República de Macedonia del Norte' },
  'MT': { en: 'Malta', es: 'Malta' },
  'NL': { en: 'Netherlands', es: 'Países Bajos' },
  'NO': { en: 'Norway', es: 'Noruega' },
  'PL': { en: 'Poland', es: 'Polonia' },
  'PT': { en: 'Portugal', es: 'Portugal' },
  'RO': { en: 'Romania', es: 'Rumanía' },
  'SE': { en: 'Sweden', es: 'Suecia' },
  'SI': { en: 'Slovenia', es: 'Eslovenia' },
  'SK': { en: 'Slovakia', es: 'Eslovaquia' },
  'TR': { en: 'Turkey', es: 'Turquía' },
};

export const mapCountryCodeToCountry = (code: CountryCode | string): Country => {
  if (!code) return { c: '', en: '', es: '' };

  const mapping = countryNameMapping[code as string];
  if (!mapping) {
    return { c: code as string, en: code as string, es: code as string };
  }

  return {
    c: code as string,
    ...mapping
  };
};


export const getOriginCountriesFromApi = (): Country[] => {
  const state = store.getState();
  const { response, loading } = state.countries.countries;

  if (loading === 'pending' || !response || response.length === 0) {
    return [];
  }

  return response.map(country => mapCountryCodeToCountry(country.code));
};

export const getDestinationCountriesForOrigin = (originCode: string): Country[] => {
  const state = store.getState();
  const { response, loading } = state.countries.countries;

  if (!originCode) {
    return getAllDestinationCountriesFromApi();
  }

  if (loading === 'pending' || !response || response.length === 0) {
    return [];
  }

  const originCountry = response.find(country => country.code === originCode);
  if (!originCountry) {
    return [];
  }

  return originCountry.validDestinationsCountryCode.map(destCode =>
    mapCountryCodeToCountry(destCode)
  );
};

export const getAllDestinationCountriesFromApi = (): Country[] => {
  const state = store.getState();
  const { response, loading } = state.countries.countries;

  if (loading === 'pending' || !response || response.length === 0) {
    return [];
  }

  const allDestinations = new Set<string>();
  response.forEach(country => {
    country.validDestinationsCountryCode.forEach(destCode => {
      allDestinations.add(destCode);
    });
  });

  return Array.from(allDestinations).map(destCode =>
    mapCountryCodeToCountry(destCode)
  );
};

export const getCountryOptions = (language: string, countries: Country[]) => {
  return countries.map(country => ({
    value: country.c,
    label: country[language as keyof Country] || country.en
  }));
};

export const isValidOriginCountry = (code: string): boolean => {

  if (!code) return false;

  const originCountries = getOriginCountriesFromApi();
  return originCountries.some(country => country.c === code);
};

export const isValidDestinationCountry = (destCode: string, originCode: string): boolean => {

  if (!destCode || !originCode) return false;

  const destinationCountries = getDestinationCountriesForOrigin(originCode);
  return destinationCountries.some(country => country.c === destCode);
};

export const getCountryName = (code: string): string => {
  if (!code) return '';

  const language = getLanguage();
  const country = mapCountryCodeToCountry(code);
  return country[language as keyof Country] || country.en;
};

export const parseCountryCode = (code: string): string => {
  return getCountryName(code);
};